<script setup>
// "You must save a seat for the following limited capacity sessions" — the
// return-visit prompt, first in the AUGUST LAUNCH process flow.
// Figma: node 4669:57041 (modal), inside 4669:56933 "Seat Reservations Open".
//
// Shown the first time a logged-in attendee returns after seat reservations
// open, listing the limited-capacity sessions already on My Schedule that still
// have no seat. Non-limited-capacity sessions keep being reserved
// asynchronously and are deliberately NOT listed here.
//
// Opened imperatively: useModal().open(SaveSeatsPrompt, { sessions }).
import { monthAbbr } from '#shared/utilities/schedule/month-abbr';

const props = defineProps({
    // { unique_id, title, start_date_and_time, end_date_and_time }
    sessions: { type: Array, default: () => [] },
});
const open = defineModel('open', { default: true });
const emit = defineEmits(['close', 'save-seats']);

// The event runs in ET and the comp shows ET explicitly, so the zone is fixed
// rather than taken from the viewer's browser — an attendee in Denver must see
// the same times printed on their badge.
const TZ = 'America/New_York';

const fmt = (value, options) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', { timeZone: TZ, ...options }).format(d);
};

// "WED • SEPT 16". The month comes from the project's AP-style list, NOT from
// Intl's `{ month: 'short' }` — that returns "Sep" where every comp says
// "SEPT". Uppercasing is left to CSS, matching the node.
const dayLabel = (s) => {
    const weekday = fmt(s.start_date_and_time, { weekday: 'short' });
    if (!weekday) return '';
    // Month index has to be read in ET too, or a late-evening session can land
    // in the previous month for a viewer west of it.
    const monthIndex = Number(fmt(s.start_date_and_time, { month: 'numeric' })) - 1;
    const day = fmt(s.start_date_and_time, { day: 'numeric' });
    return `${weekday} • ${monthAbbr(monthIndex)} ${day}`;
};

// "10:45am - 11:05am ET"
const timeLabel = (s) => {
    const t = (v) => fmt(v, { hour: 'numeric', minute: '2-digit', hour12: true })
        .replace(/\s?([AP])M/i, (_, p) => p.toLowerCase() + 'm');
    const start = t(s.start_date_and_time);
    const end = s.end_date_and_time ? t(s.end_date_and_time) : '';
    if (!start) return '';
    return end ? `${start} - ${end} ET` : `${start} ET`;
};

const rows = computed(() => (props.sessions || [])
    .filter((s) => s && s.title)
    .slice()
    .sort((a, b) => new Date(a.start_date_and_time) - new Date(b.start_date_and_time)));

// Same close-value contract as the other dialogs in this flow: the opener has
// to know whether the attendee pressed Save Seats or just dismissed. Emitting
// `close` with no payload resolves `undefined`, which reads as a dismissal and
// would silently drop the whole point of the prompt.
const result = ref({ saveSeats: false });

const handleSaveSeats = () => {
    result.value = { saveSeats: true };
    emit('save-seats');
    open.value = false;
};

const onShellClose = () => emit('close', result.value);
</script>

<template>
    <!-- `title` (not `headline`) so the shell still gets its accessible dialog
         name, while the visible content lives entirely in the default slot.
         WHY: the shell renders headline / body / actions as three separate
         blocks, and the spacing between them is its own. The comp wants a
         uniform 45px between every step of this modal (Session Modal 4669:57046
         and its child group 4669:57056 are both gap-45), which the slot
         boundaries made impossible — the gaps measured 24 / 0 / 32. Owning the
         column is the only way to hit the comp exactly. -->
    <FragmentsModalShell
        v-model:open="open"
        title="You must save a seat for the following limited capacity sessions that were added to My Schedule in order to attend."
        @close="onShellClose"
    >
        <div class="flex flex-col gap-[45px] w-full">
            <h2 class="font-hubspot-serif font-normal text-[25px] sm:text-[30px] leading-[1.3] tracking-normal text-(--magenta) text-center m-0">
                You must save a seat for the following limited capacity sessions that were added to
                My Schedule in order to attend.
            </h2>

            <!-- White notice box (4669:57060): 28/25 padding, 14px gap to the copy. -->
            <div class="flex items-start gap-[14px] w-full text-left bg-white rounded-[10px] px-[28px] py-[25px]">
                <svg class="shrink-0 mt-[4px] size-[16px] text-(--magenta)" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="8" cy="8" r="8" fill="currentColor" />
                    <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                    <circle cx="8" cy="11.6" r="0.9" fill="white" />
                </svg>
                <p class="font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] text-(--magenta) m-0">
                    If you encounter a session that is already full, you can always check back later
                    to see if any seats have opened up. There will be an onsite standby line with
                    limited seats available on a first-come, first-served basis for all limited
                    capacity sessions. We can also help you find another session to attend during
                    that time slot instead using the &ldquo;Find a Session&rdquo; feature on My Schedule.
                </p>
            </div>

            <!-- Session table (4669:57065). Hairlines are --dark-pink: a rule above
                 every row plus one below the last, so the list reads as banded. The
                 day and time columns are fixed (106 / 148) so the titles align down
                 the list regardless of how long a time string is. -->
            <ul v-if="rows.length" class="w-full list-none m-0 p-0 border-b border-(--dark-pink)">
                <!-- Mobile values are Nick's, listed on the ticket and drawn in
                     Figma 5999:151065: day/date 14px and sized to its own text
                     rather than a fixed 106px, 16px to the time, 15px of row
                     padding, and 3px down to the session name. Desktop keeps the
                     wider rhythm it already had. -->
                <li
                    v-for="s in rows"
                    :key="s.unique_id"
                    class="flex flex-col sm:flex-row sm:items-center gap-[3px] sm:gap-[30px] py-[15px] sm:py-[12px] border-t border-(--dark-pink) text-left"
                >
                    <div class="flex items-center gap-[16px] sm:gap-[30px] shrink-0">
                        <span class="w-auto sm:w-[106px] whitespace-nowrap font-hubspot-sans font-medium text-[14px] sm:text-[13px] leading-none tracking-[1.12px] sm:tracking-[1.04px] uppercase text-(--magenta)">
                            {{ dayLabel(s) }}
                        </span>
                        <span class="w-[148px] font-hubspot-sans font-normal text-[14px] leading-[1.5] tracking-[0.21px] text-(--darker-pink)">
                            {{ timeLabel(s) }}
                        </span>
                    </div>
                    <span class="flex-1 min-w-0 font-hubspot-serif font-normal text-[15px] leading-[1.4] tracking-[0.15px] text-(--magenta)">
                        {{ s.title }}
                    </span>
                </li>
            </ul>

            <!-- Takes the attendee to My Schedule's Detail view with
                 "Only Show Seats I Need to Save" pre-filtered (annotation
                 4669:65271), so the list they just read is the list they land on. -->
            <div class="flex justify-center">
                <button
                    type="button"
                    data-test="save-seats-cta"
                    class="rounded-[50px] border border-(--magenta) text-(--magenta) px-[20px] py-[10px]
                        font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] cursor-pointer
                        transition-colors duration-200 hover:bg-(--magenta) hover:text-white
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)"
                    @click="handleSaveSeats"
                >Take Me to Save Seats</button>
            </div>
        </div>

        <!-- 25px below the column, not 45: the comp puts Close outside the
             Session Modal group, at the modal's own gap (4669:57041 → 4669:57122). -->
        <div class="flex justify-center mt-[25px]">
            <button
                type="button"
                data-test="save-seats-close"
                class="bg-transparent border-none p-0 cursor-pointer
                    font-hubspot-sans font-light text-[14px] leading-[1.5] tracking-[0.28px]
                    text-(--darker-pink) underline decoration-dotted decoration-[8.5%] underline-offset-[3px]
                    transition-colors duration-200 hover:text-(--purple) hover:no-underline"
                @click="open = false"
            >Close</button>
        </div>
    </FragmentsModalShell>
</template>
